<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';

import { useAccount } from 'dashboard/composables/useAccount';

import ChannelItem from 'dashboard/components/widgets/ChannelItem.vue';
import { SOCIAL_PLATFORMS } from 'dashboard/components-next/social/constants';

const { t } = useI18n();
const router = useRouter();
const { accountId, currentAccount } = useAccount();

const globalConfig = useMapGetter('globalConfig/get');

const enabledFeatures = ref({});

const hasTiktokConfigured = computed(() => {
  return window.chatwootConfig?.tiktokAppId;
});

const hasYoutubeConfigured = computed(() => {
  return window.chatwootConfig?.googleOAuthClientId;
});

const hasMercadoLivreConfigured = computed(() => {
  return window.chatwootConfig?.mercadoLivreAppId;
});

const channelList = computed(() => {
  const { apiChannelName } = globalConfig.value;
  const channels = [
    {
      key: 'website',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WEBSITE.DESCRIPTION'),
      icon: 'i-woot-website',
    },
    {
      key: 'facebook',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.FACEBOOK.DESCRIPTION'),
      icon: 'i-woot-messenger',
    },
    {
      key: 'whatsapp',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.WHATSAPP.DESCRIPTION'),
      icon: 'i-woot-whatsapp',
    },
    {
      key: 'sms',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.SMS.DESCRIPTION'),
      icon: 'i-woot-sms',
    },
    {
      key: 'email',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.EMAIL.DESCRIPTION'),
      icon: 'i-woot-mail',
    },
    {
      key: 'api',
      title: apiChannelName || t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.API.DESCRIPTION'),
      icon: 'i-woot-api',
    },
    {
      key: 'telegram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TELEGRAM.DESCRIPTION'),
      icon: 'i-woot-telegram',
    },
    {
      key: 'line',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.LINE.DESCRIPTION'),
      icon: 'i-woot-line',
    },
    {
      key: 'instagram',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.INSTAGRAM.DESCRIPTION'),
      icon: 'i-woot-instagram',
    },
  ];

  if (hasTiktokConfigured.value) {
    channels.push({
      key: 'tiktok',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TIKTOK.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.TIKTOK.DESCRIPTION'),
      icon: 'i-ri-tiktok-fill',
    });
  }

  channels.push({
    key: 'zernio_tiktok',
    title: t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.CHANNEL_TITLE'),
    description: t('INBOX_MGMT.ADD.ZERNIO_SOCIAL.CHANNEL_DESC'),
    icon: 'i-ri-tiktok-fill',
  });

  channels.push({
    key: 'zernio_google',
    title: t('INBOX_MGMT.ADD.ZERNIO_GOOGLE.CHANNEL_TITLE'),
    description: t('INBOX_MGMT.ADD.ZERNIO_GOOGLE.CHANNEL_DESC'),
    icon: 'i-ri-google-fill',
  });

  channels.push({
    key: 'zernio_linkedin',
    title: t('INBOX_MGMT.ADD.ZERNIO_LINKEDIN.CHANNEL_TITLE'),
    description: t('INBOX_MGMT.ADD.ZERNIO_LINKEDIN.CHANNEL_DESC'),
    icon: 'i-ri-linkedin-box-fill',
  });

  // RCS sempre aparece: sem a chave na instalação, a tela avisa (422).
  channels.push({
    key: 'rcs',
    title: t('INBOX_MGMT.ADD.RCS.CHANNEL_TITLE'),
    description: t('INBOX_MGMT.ADD.RCS.CHANNEL_DESC'),
    icon: 'i-ri-message-3-fill',
  });

  if (hasYoutubeConfigured.value) {
    channels.push({
      key: 'youtube',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.YOUTUBE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.YOUTUBE.DESCRIPTION'),
      icon: 'i-ri-youtube-fill',
    });
  }

  if (hasMercadoLivreConfigured.value) {
    channels.push({
      key: 'mercado_livre',
      title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.MERCADO_LIVRE.TITLE'),
      description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.MERCADO_LIVRE.DESCRIPTION'),
      icon: 'i-ri-shopping-bag-3-fill',
    });
  }

  // Uma rede, um card (decisão Ivan 08/10): redes que já têm canal acima não
  // repetem; as outras viram canal só de publicação no Estúdio Social.
  const covered = [
    'instagram',
    'facebook',
    'tiktok',
    'linkedin',
    'googlebusiness',
  ];
  if (hasYoutubeConfigured.value) covered.push('youtube');
  SOCIAL_PLATFORMS.filter(platform => !covered.includes(platform.key)).forEach(
    platform => {
      channels.push({
        key: `social_${platform.key}`,
        title: t(
          `SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${platform.key.toUpperCase()}`
        ),
        description: t('INBOX_MGMT.ADD.SOCIAL_NETWORK.CARD_DESC'),
        icon: platform.icon,
      });
    }
  );

  // Google Ads é canal (decisão Ivan 08/10): ocupa 1 vaga do plano. A conexão
  // (popup do Google) mora em Marketing → Conexões.
  channels.push({
    key: 'google_ads',
    title: t('INBOX_MGMT.ADD.GOOGLE_ADS.CHANNEL_TITLE'),
    description: t('INBOX_MGMT.ADD.GOOGLE_ADS.CHANNEL_DESC'),
    icon: 'i-ri-google-fill',
  });

  channels.push({
    key: 'voice',
    title: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.TITLE'),
    description: t('INBOX_MGMT.ADD.AUTH.CHANNEL.VOICE.DESCRIPTION'),
    icon: 'i-ri-phone-fill',
  });

  return channels;
});

const initializeEnabledFeatures = async () => {
  enabledFeatures.value = currentAccount.value.features;
};

const initChannelAuth = channel => {
  if (channel === 'google_ads') {
    router.push({
      name: 'marketing_connections',
      params: { accountId: accountId.value },
    });
    return;
  }
  const params = {
    sub_page: channel,
    accountId: accountId.value,
  };
  router.push({ name: 'settings_inboxes_page_channel', params });
};

onMounted(() => {
  initializeEnabledFeatures();
});
</script>

<template>
  <div class="w-full p-6 overflow-auto">
    <div
      class="grid max-w-3xl grid-cols-1 xs:grid-cols-2 mx-0 gap-3 sm:grid-cols-3"
    >
      <ChannelItem
        v-for="channel in channelList"
        :key="channel.key"
        :channel="channel"
        :enabled-features="enabledFeatures"
        @channel-item-click="initChannelAuth"
      />
    </div>
  </div>
</template>
